import Vue from 'vue'
import Bugsnag from '@bugsnag/js'
import VueRouter from 'vue-router'
import goTo from 'vuetify/es5/services/goto'

import Home from '@/views/Home'
import NotFound from '@/views/404.vue'
import store from '@/store'
import { open as openModal } from './modal'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  // games
  {
    path: '/wheel',
    name: 'Wheel',
    component: () => import(/* webpackChunkName: "view-wheel" */ '@/views/games/Wheel'),
    meta: { namespace: 'wheel' },
  },
  {
    path: '/spinners',
    name: 'Spinners',
    component: () => import(/* webpackChunkName: "view-spinners" */ '@/views/games/Spinners'),
    meta: { namespace: 'spinners' },
  },
  {
    path: '/coinflip',
    name: 'Coinflip',
    component: () => import(/* webpackChunkName: "view-coinflip" */ '@/views/games/Coinflip'),
    meta: { namespace: 'coinflip' },
  },
  {
    path: '/cases',
    name: 'Cases',
    component: () => import(/* webpackChunkName: "view-cases" */ '@/views/games/Cases'),
  },
  {
    path: '/cases/:slug',
    name: 'Case',
    component: () => import(/* webpackChunkName: "view-case" */ '@/views/games/Cases/Case'),
  },
  {
    path: '/beancan-blast',
    name: 'Beancan Blast',
    component: () => import(/* webpackChunkName: "view-dice" */ '@/views/games/Dice'),
  },
  {
    path: '/crate-battles',
    name: 'Crate Battles',
    component: () => import(/* webpackChunkName: "view-case-battles" */ '@/views/games/CaseBattles'),
    meta: { namespace: 'caseBattles' },
  },
  {
    path: '/crate-battles/create',
    name: 'Crate Battle Create',
    component: () => import(
      /* webpackChunkName: "view-case-battle-create" */
      '@/views/games/CaseBattles/create-battle'
    ),
    meta: { requiresAuth: true, namespace: 'caseBattles' },
  },
  {
    path: '/crate-battles/:id/:replay?',
    name: 'Crate Battle',
    component: () => import(/* webpackChunkName: "view-case-battle" */ '@/views/games/CaseBattles/Battle'),
    meta: { namespace: 'caseBattles' },
  },
  //
  {
    path: '/leaderboards',
    name: 'Leaderboards',
    component: () => import(/* webpackChunkName: "view-leaderboards" */ '@/views/Leaderboards'),
  },
  {
    path: '/deposit',
    name: 'Deposit',
    component: () => import(/* webpackChunkName: "view-deposit" */ '@/views/Deposit/deposit.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/deposit/offers',
    name: 'Offers',
    component: () => import(/* webpackChunkName: "view-offers" */ '@/views/Deposit/views/Offers/offers.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/shop',
    name: 'Shop',
    component: () => import(/* webpackChunkName: "view-shop" */ '@/views/shop'),
    meta: { requiresAuth: true },
  },
  {
    path: '/promo/giveaway',
    name: 'Giveaway',
    component: () => import(/* webpackChunkName: "view-giveaway" */ '@/views/giveaway'),
  },
  {
    path: '/promo/claim-rewards',
    name: 'claim-rewards',
    component: () => import(/* webpackChunkName: "claim-rewards" */ '@/views/claim-rewards.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/terms',
    name: 'tos',
    component: () => import(/* webpackChunkName: "tos" */ '@/views/tos.vue'),
  },
  {
    path: '/refund-policy',
    name: 'refund',
    component: () => import(/* webpackChunkName: "refund" */ '@/views/refund.vue'),
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import(/* webpackChunkName: "privacy" */ '@/views/privacy.vue'),
  },
  {
    path: '/fairness',
    name: 'fairness',
    component: () => import(/* webpackChunkName: "fairness" */ '@/views/fairness.vue'),
  },
  // virtual routes
  {
    path: '/r/:refCode',
    name: 'referral',
    beforeEnter: (to, from, next) => {
      const { refCode } = to.params

      if (refCode) {
        localStorage.setItem('referredBy', refCode)
      }

      next({ name: 'Home' })
    },
  },
  {
    path: '*',
    name: '404',
    component: NotFound,
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    let scrollTo = 0

    if (to.hash) {
      scrollTo = to.hash
    } else if (savedPosition) {
      scrollTo = savedPosition.y
    }

    return goTo(scrollTo)
  },
})

function waitForGetter(getter) {
  return new Promise(res => {
    const unsub = store.watch(getter, val => {
      res(val)
      unsub()
    })
  })
}

router.beforeEach(async (to, from, next) => {
  // avoid endless error loop
  if (to.name === '404') {
    next()
    return
  }

  // this route requires auth, check if logged in
  if (to.matched.some(record => record.meta.requiresAuth)) {
    let session = store.getters['user/session']

    // if too early, wait for getter to be filled
    if (session === undefined) {
      session = await waitForGetter(state => state.user.session)
    }

    // if not, redirect to login page.
    if (!session) {
      next({
        name: from?.name ?? 'Home',
        query: { authRedirect: to.fullPath },
      })

      openModal('auth', { maxWidth: 320, persistent: true })

      return
    }
  }

  if (to.meta.namespace || from.meta.namespace) {
    if (from.meta.namespace === to.meta.namespace) {
      next()
      return
    }

    try {
      // wait for app to be loaded
      if (!store.getters.loaded) {
        await waitForGetter(state => state.loaded)
      }

      // unsub from last
      if (from.meta.namespace) {
        await store.dispatch('unsubscribe', from.meta.namespace)
      }

      // sub to new
      if (to.meta.namespace) {
        await store.dispatch('subscribe', to.meta.namespace)
      }

      next()
    } catch (error) {
      Bugsnag.notify(error, event => {
        event.addMetadata('route', { to: to.name, from: from?.name })
      })
      next({ name: '404', params: { 0: 'ns-subscription-error' } })
    }

    return
  }

  next()
})

export default router
